// src/components/Footer.jsx
import React, { useEffect, useState } from 'react';
import AboutModal from './AboutModal';
import { API_BASE } from '../../config/runtime';

const Footer = () => {
  const [modalType, setModalType] = useState(null);
  const [branches, setBranches] = useState([]);

  const normalizeLocationName = (name) =>
    String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

  const defaultLocations = [
    { name: 'SM Seaside City Cebu' },
    { name: 'Gmall of Cebu' },
    { name: 'Island Central Mall Mactan' },
    { name: 'Ayala Malls Central Bloc' },
    { name: 'Ororama Cagayan' },
  ];

  useEffect(() => {
    fetch(`${API_BASE}/get_branches.php`)
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.branches)) setBranches(data.branches);
      })
      .catch(() => setBranches([]));
  }, []);

  const displayLocations = [
    ...branches,
    ...defaultLocations.filter(
      loc => !branches.some(
        b => normalizeLocationName(b.branch_name) === normalizeLocationName(loc.name)
      )
    ),
  ];

  const buildMapsLink = (locationName, address) => {
    const query = `Hygea Wellness Spa ${locationName}${address ? `, ${address}` : ''}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  return (
    <>
      <footer style={{ backgroundColor: '#EAE3DB' }} className="relative overflow-hidden">

        {/* Subtle decorative top border */}
        <div className="h-px w-full" style={{ background: 'linear-gradient(to right, transparent, #c4a882, transparent)' }} />

        {/* Main content */}
        <div className="max-w-7xl mx-auto px-8 lg:px-12 pt-16 pb-12">

          {/* Brand header row */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14 pb-10"
            style={{ borderBottom: '1px solid rgba(139,94,60,0.15)' }}>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.45em] mb-2"
                style={{ color: '#c4956a' }}>
                Est. 2026
              </p>
              <h2 className="text-3xl font-black tracking-tight leading-none"
                style={{ color: '#1a1009', fontFamily: 'Georgia, serif' }}>
                Hygea Wellness
                <span style={{ color: '#8b5e3c' }}>.</span>
              </h2>
              <p className="text-sm mt-2 font-medium" style={{ color: '#a89080' }}>
                Rejuvenate your body &amp; mind
              </p>
            </div>
            <div className="flex items-center gap-6">
              <a href="tel:09773824416"
                className="flex items-center gap-2.5 group transition-opacity hover:opacity-70">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(139,94,60,0.12)' }}>
                  <svg className="h-3.5 w-3.5" style={{ color: '#8b5e3c' }} viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <span className="text-sm font-semibold" style={{ color: '#3a2010' }}>0977 382 4416</span>
              </a>
              <a href="mailto:hygeawellnessspa@gmail.com"
                className="flex items-center gap-2.5 group transition-opacity hover:opacity-70">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(139,94,60,0.12)' }}>
                  <svg className="h-3.5 w-3.5" style={{ color: '#8b5e3c' }} viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </div>
                <span className="text-sm font-semibold" style={{ color: '#3a2010' }}>
                  hygeawellnessspa@gmail.com
                </span>
              </a>
            </div>
          </div>

          {/* Three-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">

            {/* About */}
            <div>
              <h4 className="text-[10px] font-black uppercase tracking-[0.4em] mb-6"
                style={{ color: '#8b5e3c' }}>
                About Hygea
              </h4>
              <ul className="space-y-1">
                {[
                  { label: 'Our Story', type: 'story' },
                  { label: 'Our Mission', type: 'mission' },
                  { label: 'Our Vision', type: 'vision' },
                ].map(({ label, type }) => (
                  <li key={type}>
                    <button
                      onClick={() => setModalType(type)}
                      className="group flex items-center gap-3 py-2 w-full text-left transition-all"
                    >
                      <span className="w-4 h-px flex-shrink-0 transition-all duration-300 group-hover:w-6"
                        style={{ backgroundColor: '#c4956a' }} />
                      <span className="text-sm font-medium transition-colors duration-200"
                        style={{ color: '#3a2010' }}
                        onMouseEnter={e => e.target.style.color = '#8b5e3c'}
                        onMouseLeave={e => e.target.style.color = '#3a2010'}>
                        {label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Decorative element */}
              <div className="mt-10 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(139,94,60,0.12)' }}>
                  <svg className="w-3 h-3" style={{ color: '#8b5e3c' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: '#c4a882' }}>
                  Wellness for all
                </span>
              </div>
            </div>

            {/* Branches — spans 2 columns */}
            <div className="md:col-span-2">
              <h4 className="text-[10px] font-black uppercase tracking-[0.4em] mb-6"
                style={{ color: '#8b5e3c' }}>
                Our Branches
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                {displayLocations.map((location) => {
                  const locationName = location.branch_name || location.name;
                  const locationAddress = location.address || '';
                  const workingHours = location.working_hours || '';
                  const showWorkingHours = workingHours && String(workingHours).trim().length > 0;
                  const fbLink = location.fb_page || location.facebook;

                  return (
                    <div
                      key={location.branch_id || locationName}
                      className="group py-3 flex flex-col gap-1.5"
                      style={{ borderBottom: '1px solid rgba(139,94,60,0.1)' }}
                    >
                      <div className="flex items-center gap-2">
                        {/* Location pin */}
                        <svg className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#c4956a' }}
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round"
                            d="M12 21s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12z" />
                          <circle cx="12" cy="9" r="2.5" />
                        </svg>

                        <a
                          href={buildMapsLink(locationName, locationAddress)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-semibold leading-snug transition-colors duration-200"
                          style={{ color: '#1a1009' }}
                          onMouseEnter={e => e.target.style.color = '#8b5e3c'}
                          onMouseLeave={e => e.target.style.color = '#1a1009'}
                        >
                          {locationName}
                        </a>

                        {/* Facebook link */}
                        {fbLink && (
                          <a href={fbLink} target="_blank" rel="noreferrer"
                            aria-label={`${locationName} on Facebook`}
                            className="ml-auto flex-shrink-0 transition-opacity hover:opacity-70"
                            style={{ color: '#1877F2' }}>
                            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
                            </svg>
                          </a>
                        )}
                      </div>

                      {/* Hours badge */}
                      {showWorkingHours && (
                        <div className="flex items-center gap-1.5 pl-5">
                          <svg className="h-3 w-3 flex-shrink-0" style={{ color: '#a89080' }}
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path strokeLinecap="round" d="M12 6v6l4 2" />
                          </svg>
                          <span className="text-[11px] font-medium" style={{ color: '#8b6b52' }}>
                            {workingHours}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid rgba(139,94,60,0.15)', backgroundColor: 'rgba(139,94,60,0.05)' }}
          className="px-8 lg:px-12 py-5">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[11px] uppercase tracking-[0.35em] font-medium" style={{ color: '#a89080' }}>
              &copy; 2026 Hygea Wellness Spa. All Rights Reserved.
            </p>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#c4956a' }} />
              <p className="text-[11px] uppercase tracking-[0.25em] font-medium" style={{ color: '#c4a882' }}>
                Your wellness is our priority
              </p>
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#c4956a' }} />
            </div>
          </div>
        </div>

      </footer>

      <AboutModal
        isOpen={modalType !== null}
        modalType={modalType}
        closeModal={() => setModalType(null)}
      />
    </>
  );
};

export default Footer;